'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contextualNodeAttributes"];
import tinymce from 'tinymce';
import { getFetchBrandingCustomFonts } from 'tinymce-data/api/Fonts';
import GroupedFontSelect from 'rich-text-lib/components/formatting/fontSelect/GroupedFontSelect';
import { memoize } from 'rich-text-lib/utils/functions';
import { FORMATTING_SETTINGS, hsformattingConfig, UnsupportedFonts } from '../constants/hsformatting';
import { getPluginSetting } from '../utils';
import { CANVAS_FONT_FAMILY } from '../constants/toolbar';
import { getFontSelectOptions, matchFont, calculateParsedFonts, constructParsedFontText, getHandleChangeFont, getCustomFontSelectOptions, addCustomFontPostProcessTransform, addCustomFontPreProcessTransform, injectCustomFontFaceDeclarations, getBrandingFonts } from '../utils/formatting';
import { maybeGetComputedStyle } from '../utils/getComputedStyle';
import { applyFontStyles } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
export function addFontSelects(editor) {
  const {
    [hsformattingConfig.FONT_FORMATS]: fontFormats,
    [hsformattingConfig.WEBFONT_FORMATS]: webFontFormats,
    [hsformattingConfig.ENABLE_PARSED_FONTS]: enableParsedFonts,
    [hsformattingConfig.USE_PARSED_FONTS]: useParsedFonts,
    [hsformattingConfig.USE_BRANDING_FONTS]: useBrandingFonts
  } = getPluginSetting(editor, FORMATTING_SETTINGS);
  const defaultFonts = getFontSelectOptions(fontFormats.defaults);
  const customFonts = getCustomFontSelectOptions(fontFormats.customFonts || []);
  const webfonts = getFontSelectOptions(webFontFormats);
  const isEmail = getPluginSetting(editor, 'isEmail');
  addCustomFontPreProcessTransform(editor);
  if (useBrandingFonts) {
    const getBrandFonts = getFetchBrandingCustomFonts(editor.settings.apiClient);
    tinymce.canvas.addIdleCallback(editor, null, () => {
      getBrandFonts().then(_brandFonts => {
        // add combined custom font data to editor plugins
        // used in controls plugin to disable bold/italic button for both custom font
        const brandingFonts = getCustomFontSelectOptions(_brandFonts && _brandFonts.length ? _brandFonts : []);
        const combinedFonts = [...brandingFonts, ...customFonts];
        editor.plugins.hsformatting.brandingFonts = brandingFonts;
        editor.plugins.hsformatting.combinedCustomFonts = combinedFonts;
      }).catch(err => {
        console.error(err);
        editor.plugins.hsformatting.combinedCustomFonts = customFonts;
      }).finally(() => {
        addCustomFontPostProcessTransform(editor);
        injectCustomFontFaceDeclarations(editor);
      });
    });
  } else {
    addCustomFontPostProcessTransform(editor, customFonts);
    injectCustomFontFaceDeclarations(editor, customFonts);
  }
  editor.addCommand('hsfontselect', (_command, {
    value,
    isCustomFont
  }) => {
    const fontApplied = isCustomFont ? value.fontValue.optionValue : value;
    editor.formatter.apply('fontname', {
      value: fontApplied
    });
    if (isCustomFont) {
      applyFontStyles(editor, value.fontValue.styles);
    }
    editor.nodeChanged();
  });
  const getParsedFonts = memoize(calculateParsedFonts, _editor => _editor.id);
  const parsedFontText = constructParsedFontText(isEmail);
  const getFormattingAttributes = ({
    element
  }) => {
    const computedStyle = maybeGetComputedStyle(editor.getWin(), element);
    if (!computedStyle) {
      return {};
    }
    const {
      parsedFonts,
      fontsWithUsedUnsupportedFonts
    } = getParsedFonts(editor, useParsedFonts, defaultFonts, webfonts);
    const matched = matchFont(computedStyle.fontFamily, [...defaultFonts, ...customFonts, ...webfonts, ...parsedFonts, ...getBrandingFonts(editor)]);
    const value = matched ? matched.value : null;
    return {
      parsedFonts,
      fontsWithUsedUnsupportedFonts,
      [CANVAS_FONT_FAMILY]: value
    };
  };
  tinymce.canvas.addNodeChangeHandler(editor, {
    getFormattingAttributes
  });
  tinymce.canvas.addButton(editor, CANVAS_FONT_FAMILY, {
    renderControl: props => {
      const {
          contextualNodeAttributes
        } = props,
        rest = _objectWithoutPropertiesLoose(props, _excluded);
      const {
        [CANVAS_FONT_FAMILY]: value,
        parsedFonts = [],
        fontsWithUsedUnsupportedFonts = []
      } = contextualNodeAttributes;
      return /*#__PURE__*/_jsx(GroupedFontSelect, Object.assign({
        use: "on-dark",
        fonts: fontsWithUsedUnsupportedFonts,
        defaultFonts: fontsWithUsedUnsupportedFonts,
        webfonts: webfonts,
        parsedFonts: parsedFonts,
        enableParsedFonts: enableParsedFonts,
        customFonts: customFonts,
        fetchBrandingCustomFonts: useBrandingFonts && getFetchBrandingCustomFonts(editor.settings.apiClient),
        value: value,
        onChange: getHandleChangeFont(editor),
        parsedFontText: parsedFontText,
        unsupportedFonts: UnsupportedFonts
      }, rest));
    }
  });
}